import { useQuery, useInfiniteQuery, useMutation, useQueryClient, Query } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { addToast } from '@heroui/react';
import guidesApi, {
  Guide,
  CreateGuideRequest,
  GenerateGuideFromPdfRequest,
  GuideDifficulty,
  GuideLanguage,
  UpdateGuideRequest,
  AddStepRequest,
  ContentDepth,
} from '@/services/apis/guides';
import { ApiResponse } from '@/services/apis/base';
import { CreateGuideData } from '../components/admin/types';
import { useLanguage } from '../contexts/LanguageContext';

// Query keys
export const guideKeys = {
  all: ['guides'] as const,
  lists: () => [...guideKeys.all, 'list'] as const,
  list: (filters: Record<string, any>) => [...guideKeys.lists(), filters] as const,
  details: () => [...guideKeys.all, 'detail'] as const,
  detail: (id: string) => [...guideKeys.details(), id] as const,
  byCategory: (category: string) => [...guideKeys.all, 'category', category] as const,
  stats: () => [...guideKeys.all, 'stats'] as const,
  userMetrics: (language: string) => [...guideKeys.all, 'user', 'metrics', language] as const,
  userUnfinishedGuides: (language: string) => [...guideKeys.all, 'user', 'unfinished-guides', language] as const,
  userRecommended: (language: string) => [...guideKeys.all, 'user', 'recommended', language] as const,
  progress: (guideId: string, language: string) => [...guideKeys.all, 'progress', guideId, language] as const,
};

export interface GuidesFilters {
  limit?: number;
  language?: string;
  productId?: string;
  categoryId?: string;
  categoryIds?: string[];
  isPublished?: boolean;
  search?: string;
  sortBy?: string;
  sortOrder?: 'asc' | 'desc';
  useDraftData?: boolean;
}

// ==================== QUERY HOOKS ====================

// Hooks for fetching guides with infinite scroll
export const useInfiniteGuides = (filters: GuidesFilters = {}) => {
  const {
    limit = 10,
    language = 'en',
    productId,
    categoryId,
    categoryIds,
    isPublished,
    search,
    sortBy = 'createdAt',
    sortOrder = 'desc',
    useDraftData = false,
  } = filters;

  return useInfiniteQuery({
    queryKey: guideKeys.list({
      limit,
      language,
      productId,
      categoryId,
      categoryIds,
      isPublished,
      search,
      sortBy,
      sortOrder,
      useDraftData,
    }),
    queryFn: async ({ pageParam }) => {
      const response = await guidesApi.getGuides({
        cursor: pageParam,
        limit,
        language,
        productId,
        categoryId,
        categoryIds,
        isPublished,
        search,
        sortBy,
        sortOrder,
        useDraftData,
      });
      return response;
    },
    getNextPageParam: (lastPage) => {
      return lastPage.pageInfo?.cursor || undefined;
    },
    initialPageParam: undefined as string | undefined,
    staleTime: 1000 * 60 * 5, // 5 minutes
  });
};

export const useGuide = ({
  id,
  shouldSkip,
  refetchInterval,
}: {
  id: string;
  shouldSkip: boolean;
  refetchInterval?: number | false | ((query: Query<ApiResponse<Guide>, Error>) => number | false);
}) => {
  return useQuery<ApiResponse<Guide>, Error>({
    queryKey: guideKeys.detail(id),
    queryFn: () => guidesApi.getGuide(id),
    enabled: !!id && !shouldSkip,
    refetchInterval,
  });
};

export const useGuidesByCategory = (category: string, page: number = 1, limit: number = 10) => {
  return useQuery({
    queryKey: guideKeys.byCategory(category),
    queryFn: () => guidesApi.getGuidesByCategory(category, page, limit),
    enabled: !!category,
  });
};

export const useGuideStats = () => {
  return useQuery({
    queryKey: guideKeys.stats(),
    queryFn: () => guidesApi.getGuideStats(),
    staleTime: 1000 * 60, // 1 minute
  });
};

export const usePublishedGuideVersion = (id: string) => {
  return useQuery<ApiResponse<Guide>, Error>({
    queryKey: [...guideKeys.detail(id), 'published'],
    queryFn: () => guidesApi.getLatestPublishedVersion(id),
    enabled: !!id,
    staleTime: 1000 * 60 * 5, // 5 minutes
  });
};

// ==================== MUTATION HOOKS ====================

export const useCreateGuide = () => {
  const navigate = useNavigate();
  const { t } = useTranslation('admin');

  return useMutation({
    mutationFn: async (data: CreateGuideData) => {
      if (data.mode === 'pdf' && data.fileId) {
        // Handle PDF import flow - fileId is already provided from file metadata
        const guideData: GenerateGuideFromPdfRequest = {
          title: 'New generated draft guide',
          difficulty: GuideDifficulty.BEGINNER,
          fileId: data.fileId,
          processImages: data.processImages ?? false,
          contentDepth: (data.contentDepth as ContentDepth) ?? ContentDepth.SIMPLIFIED,
        };

        return guidesApi.generateGuideFromPdf(guideData);
      } else if (data.mode === 'manual') {
        // Handle manual creation flow with default data
        const guideData: CreateGuideRequest = {
          title: 'New draft guide',
          difficulty: GuideDifficulty.BEGINNER,
        };

        return guidesApi.createGuide(guideData);
      } else {
        throw new Error('Invalid creation mode or missing required data');
      }
    },
    onSuccess: (response) => {
      addToast({
        title: t('guides.modal.form.createSuccess'),
        color: 'success',
      });
      navigate(`/admin/guides/builder/${response.data?.id}`);
    },
    onError: (error: Error) => {
      console.error('Error creating guide:', error);
      addToast({
        title: error.message || t('guides.modal.form.createError'),
        color: 'danger',
      });
    },
  });
};

export const useUpdateGuideDetails = (guideId: string) => {
  const queryClient = useQueryClient();
  const { t } = useTranslation('admin');

  return useMutation({
    mutationFn: async ({ lang, data }: { lang: GuideLanguage; data: UpdateGuideRequest }) => {
      return guidesApi.updateGuide(guideId, data, lang);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: guideKeys.detail(guideId) });
      addToast({
        title: t('guides.builder.updateSuccess'),
        color: 'success',
      });
    },
    onError: (error: Error) => {
      console.error('Error updating guide translation:', error);
      addToast({
        title: error.message || t('guides.builder.updateError'),
        color: 'danger',
      });
    },
  });
};

export const useUpdateStep = (guideId: string) => {
  const queryClient = useQueryClient();
  const { t } = useTranslation('admin');
  const { currentLanguage } = useLanguage();

  return useMutation({
    mutationFn: async ({
      stepId,
      data,
      lang,
    }: {
      stepId: string;
      data: Partial<AddStepRequest>;
      lang?: GuideLanguage;
    }) => {
      return guidesApi.updateStep(guideId, stepId, data, lang || (currentLanguage as GuideLanguage));
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: guideKeys.detail(guideId) });
      addToast({
        title: t('guides.builder.stepUpdateSuccess'),
        color: 'success',
      });
    },
    onError: (error: Error) => {
      console.error('Error updating step:', error);
      addToast({
        title: error.message || t('guides.builder.stepUpdateError'),
        color: 'danger',
      });
    },
  });
};

export const useAddStep = (guideId: string) => {
  const queryClient = useQueryClient();
  const { t } = useTranslation('admin');

  return useMutation({
    mutationFn: async (data: AddStepRequest) => {
      return guidesApi.addStep(guideId, data);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: guideKeys.detail(guideId) });
      addToast({
        title: t('guides.builder.stepAddSuccess'),
        color: 'success',
      });
    },
    onError: (error: Error) => {
      console.error('Error adding step:', error);
      addToast({
        title: error.message || t('guides.builder.stepAddError'),
        color: 'danger',
      });
    },
  });
};

export const usePublishGuide = (guideId: string) => {
  const queryClient = useQueryClient();
  const { t } = useTranslation('admin');

  return useMutation({
    mutationFn: async () => {
      return guidesApi.publishGuide(guideId);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: guideKeys.detail(guideId) });
      queryClient.invalidateQueries({ queryKey: guideKeys.lists() });
      queryClient.invalidateQueries({ queryKey: guideKeys.stats() });
      addToast({
        title: t('guides.builder.publishSuccess'),
        color: 'success',
      });
    },
    onError: (error: any) => {
      console.error('Error publishing guide:', error);

      // Check if it's a BadRequest (400) error and show the error message
      if (error?.status === 400 && error?.message) {
        addToast({
          title: error.message,
          color: 'danger',
        });
      } else {
        addToast({
          title: error.message || t('guides.builder.publishError'),
          color: 'danger',
        });
      }
    },
  });
};

export const useUnpublishGuide = (guideId?: string) => {
  const queryClient = useQueryClient();
  const { t } = useTranslation('admin');

  return useMutation({
    mutationFn: async (id?: string) => {
      const targetId = id || guideId;
      if (!targetId) {
        throw new Error('Guide ID is required');
      }
      return guidesApi.unpublishGuide(targetId);
    },
    onSuccess: () => {
      if (guideId) {
        queryClient.invalidateQueries({ queryKey: guideKeys.detail(guideId) });
      }
      queryClient.invalidateQueries({ queryKey: guideKeys.lists() });
      queryClient.invalidateQueries({ queryKey: guideKeys.stats() });
      addToast({
        title: t('guides.builder.unpublishSuccess'),
        color: 'success',
      });
    },
    onError: (error: Error) => {
      console.error('Error unpublishing guide:', error);
      addToast({
        title: error.message || t('guides.builder.unpublishError'),
        color: 'danger',
      });
    },
  });
};

export const useDeleteGuide = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => guidesApi.deleteGuide(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: guideKeys.lists() });
      queryClient.invalidateQueries({ queryKey: guideKeys.stats() });
    },
  });
};

export const useDeleteStep = (guideId: string) => {
  const queryClient = useQueryClient();
  const { t } = useTranslation('admin');

  return useMutation({
    mutationFn: async (stepId: string) => {
      return guidesApi.deleteStep(guideId, stepId);
    },
    onMutate: async (stepId: string) => {
      await queryClient.cancelQueries({ queryKey: guideKeys.detail(guideId) });
      const previousGuide = queryClient.getQueryData<{ data: Guide }>(guideKeys.detail(guideId));
      if (previousGuide) {
        queryClient.setQueryData<{ data: Guide }>(guideKeys.detail(guideId), {
          ...previousGuide,
          data: {
            ...previousGuide.data,
            steps: previousGuide.data.steps.filter((step) => step.id !== stepId),
          },
        });
      }
      return { previousGuide };
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: guideKeys.detail(guideId) });
      addToast({
        title: t('guides.builder.stepDeleteSuccess'),
        color: 'success',
      });
    },
    onError: (error: Error, _stepId: string, context) => {
      if (context?.previousGuide) {
        queryClient.setQueryData(guideKeys.detail(guideId), context.previousGuide);
      }
      console.error('Error deleting step:', error);
      addToast({
        title: error.message || t('guides.builder.stepDeleteError'),
        color: 'danger',
      });
    },
  });
};

export const useTranslateGuide = (guideId: string) => {
  const queryClient = useQueryClient();
  const { t } = useTranslation('admin');

  return useMutation({
    mutationFn: async (targetLanguage: GuideLanguage) => {
      return guidesApi.translateGuide(guideId, targetLanguage);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: guideKeys.detail(guideId) });
      addToast({
        title: t('guides.builder.translateSuccess'),
        color: 'success',
      });
    },
    onError: (error: Error) => {
      console.error('Error translating guide:', error);
      addToast({
        title: error.message || t('guides.builder.translateError'),
        color: 'danger',
      });
    },
  });
};

// ==================== GUIDE PROGRESS HOOKS ====================

export const useGuideProgress = (guideId: string, language: GuideLanguage, enabled: boolean = true) => {
  return useQuery({
    queryKey: guideKeys.progress(guideId, language),
    queryFn: () => guidesApi.getGuideProgress(guideId, language),
    enabled: !!guideId && !!language && enabled,
    staleTime: 1000 * 60, // 1 minute
  });
};

export const useUpdateGuideProgress = (guideId: string, language: GuideLanguage) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (progress: {
      currentStepId?: string | null;
      completedStepIds?: string[];
      quizScores?: Record<string, number>;
    }) => {
      return guidesApi.updateGuideProgress(guideId, language, progress);
    },
    onMutate: async () => {
      // Capture the previous progress to detect completion state change
      const previousProgress = queryClient.getQueryData<{ data: { isCompleted: boolean } }>(
        guideKeys.progress(guideId, language),
      );
      return { previousIsCompleted: previousProgress?.data?.isCompleted ?? false };
    },
    onSuccess: (response, _, context) => {
      queryClient.setQueryData(guideKeys.progress(guideId, language), response);

      // Check if guide completion status changed
      const newIsCompleted = response.data?.isCompleted ?? false;
      const wasCompleted = context?.previousIsCompleted ?? false;

      queryClient.invalidateQueries({ queryKey: ['courses', 'guides-progress'] });
      if (newIsCompleted !== wasCompleted) {
        // Guide completion status changed - invalidate all course-related caches
        // since the backend syncs course completion status when a guide is completed/un-completed
        queryClient.invalidateQueries({ queryKey: ['courses', 'current-user-progress'] });
        queryClient.invalidateQueries({ queryKey: ['courses', 'user', 'resume'] });
        queryClient.invalidateQueries({ queryKey: ['courses', 'user', 'recommended'] });
      }
    },
    onError: (error: Error) => {
      console.error('Failed to save progress:', error);
    },
  });
};

// ==================== USER-SPECIFIC HOOKS ====================

export const useUserMetrics = (language: string) => {
  return useQuery({
    queryKey: guideKeys.userMetrics(language),
    queryFn: () => guidesApi.getUserMetrics(language as GuideLanguage),
    enabled: !!language,
    staleTime: 1000 * 60 * 5, // 5 minutes
  });
};

export const useInfiniteUserUnfinishedGuides = (language: string) => {
  return useInfiniteQuery({
    queryKey: guideKeys.userUnfinishedGuides(language),
    queryFn: async ({ pageParam }) => {
      const response = await guidesApi.getUserUnfinishedGuides({
        language: language as GuideLanguage,
        cursor: pageParam,
        limit: 10,
      });
      return response;
    },
    getNextPageParam: (lastPage) => {
      return lastPage.pageInfo?.cursor || undefined;
    },
    initialPageParam: undefined as string | undefined,
    enabled: !!language,
    staleTime: 1000 * 60 * 5, // 5 minutes
  });
};

export const useInfiniteRecommendedGuides = (language: string) => {
  return useInfiniteQuery({
    queryKey: guideKeys.userRecommended(language),
    queryFn: async ({ pageParam }) => {
      const response = await guidesApi.getRecommendedGuides({
        language: language as GuideLanguage,
        cursor: pageParam,
        limit: 6,
      });
      return response;
    },
    getNextPageParam: (lastPage) => {
      return lastPage.pageInfo?.cursor || undefined;
    },
    initialPageParam: undefined as string | undefined,
    enabled: !!language,
    staleTime: 1000 * 60 * 5, // 5 minutes
  });
};
